import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from '../../services/dialog.service';
import { DataService } from '../../services/data.service';
import { DataSourceGet, DataSourceCreate } from '../../models/datasource';
import { NewDataSourceDialogComponent, DataSourceFormData } from '../../components/new-data-source-dialog/new-data-source-dialog.component';
import { EditDataSourceDialogComponent, EditDataSourceDialogData, EditDataSourceResult } from '../../components/edit-datasource/edit-datasource-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-data-sources',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule, 
    MatButtonModule, 
    MatIconModule, 
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatSnackBarModule,
    RouterModule
  ],
  templateUrl: './data-sources.component.html',
  styleUrls: ['./data-sources.component.scss']
})
export class DataSourcesComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ['icon', 'dataSourceName', 'type', 'tags', 'projectCount', 'createdAt', 'updatedAt', 'actions'];
  
  dataSource = new MatTableDataSource<DataSourceGet>([]);
  searchQuery = '';
  loading = false;
  error: string | null = null;

  @ViewChild('sortTable', { static: false }) sort!: MatSort;

  constructor(
    private dialogService: DialogService,
    private dataService: DataService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    // Set up custom sorting for derived columns
    this.dataSource.sortingDataAccessor = (item: DataSourceGet, property: string) => {
      switch (property) {
        case 'dataSourceName':
          return (item.dataSourceName || '').toLowerCase();
        case 'type':
          return this.getDataSourceTypeLabel(item.dataSourceTypeId).toLowerCase();
        case 'projectCount':
          return item.projectCount || 0;
        case 'createdAt':
          return item.createdAt ? new Date(item.createdAt).getTime() : 0;
        case 'updatedAt':
          return item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
        default:
          return '';
      }
    };

    // Set up custom filtering to search across multiple fields
    this.dataSource.filterPredicate = (data: DataSourceGet, filter: string) => {
      const searchStr = filter.toLowerCase();
      const nameMatch = data.dataSourceName?.toLowerCase().includes(searchStr);
      const tagsMatch = data.tags?.toLowerCase().includes(searchStr);
      const typeMatch = this.getDataSourceTypeLabel(data.dataSourceTypeId)?.toLowerCase().includes(searchStr);
      return nameMatch || tagsMatch || typeMatch;
    };
  }

  ngOnInit(): void {
    this.loadDataSources();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.cdr.detectChanges();
  }

  loadDataSources(): void {
    this.loading = true;
    this.error = null;
    
    this.dataService.getDataSourceList().subscribe({
      next: (dataSources) => {
        this.dataSource.data = dataSources;
        this.loading = false;
        // Set sort after data loads and apply default sort
        setTimeout(() => {
          if (this.sort) {
            this.dataSource.sort = this.sort;
            this.sort.sort({ id: 'updatedAt', start: 'desc', disableClear: false });
          }
        });
      },
      error: (error) => {
        this.error = error.message;
        this.loading = false;
        console.error('Error loading data sources:', error);
        this.snackBar.open(error.message || 'Failed to load data sources', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  formatDate(date: Date | string | null | undefined): string {
    // Handle null, undefined, or empty values
    if (!date) {
      return '-';
    }
    
    try {
      // Handle both Date objects and date strings
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Check if the date is valid
      if (!dateObj || isNaN(dateObj.getTime())) {
        return 'Invalid Date';
      }
      
      // Format the date as DD/MM/YYYY with time
      return dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) + ' ' + dateObj.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  formatTags(tags: string | null | undefined): string[] {
    if (!tags) {
      return [];
    }
    
    // Split by comma and clean up whitespace
    return tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  }

  applyFilter(query: string): void {
    this.dataSource.filter = query.trim().toLowerCase();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.dataSource.filter = '';
  }

  getDataSourceIcon(dataSource: DataSourceGet): string {
    // Return icon based on dataSourceTypeId
    switch (dataSource.dataSourceTypeId) {
      case 1: // API Listener
        return 'upload_file';
      case 2: // API Retriever
        return 'cloud_download';
      case 3: // Uploaded File
        return 'description';
      default:
        return 'data-set';
    }
  }

  getDataSourceTypeLabel(typeId: number | undefined): string {
    switch (typeId) {
      case 1:
        return 'API Listener';
      case 2:
        return 'API Retriever';
      case 3:
        return 'Uploaded File';
      default:
        return 'Unknown';
    }
  }

  onProjectsClick(dataSource: DataSourceGet): void {
    // Navigate to projects filtered by this data source
    console.log('Navigate to projects for data source:', dataSource.companyId);
  }

  onProjectCountClick(event: Event, dataSource: DataSourceGet): void {
    event.stopPropagation();
    // Navigate to activity page filtered by this datasource
    this.router.navigate(['/activity'], {
      queryParams: {
        datasourceId: dataSource.dataSourceId,
        datasourceName: dataSource.dataSourceName
      }
    });
  }

  openNewDataSourceDialog(projectId?: string): void {
    const dialogRef = this.dialogService.openDialog(NewDataSourceDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { 
        title: 'New Data Source',
        projectId: projectId,
        showExistingDataSources: false // Don't show existing sources in data source list screen
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data) {
        this.loadDataSources();
      }
    });
  }

  createDataSource(data: DataSourceFormData): void {
    // Convert dialog form data to DataSourceCreate model
    const newDataSource: DataSourceCreate = {
      dataSourceName: data.name || `New ${data.connectionType} Source`,
      dataSourceTypeId: this.getDataSourceTypeId(data.connectionType),
      tags: data.connectionType,
      dataSourceAccessJson: JSON.stringify({
        type: data.connectionType,
        formData: data
      })
    };

    // Check if datasource name already exists
    this.dataService.checkDataSourceExists(newDataSource.dataSourceName).subscribe({
      next: (existsResult) => {
        if (existsResult.exists) {
          console.error('Data source name already exists:', newDataSource.dataSourceName);
          // TODO: Show error to user via snackbar
          return;
        }
        
        this.dataService.createDataSource(newDataSource).subscribe({
          next: (guid) => {
            console.log('Data source created with GUID:', guid);
            // Refresh the data sources list
            this.loadDataSources();
          },
          error: (error) => {
            console.error('Error creating data source:', error);
            // Handle error - you might want to show a snackbar or error message
          }
        });
      },
      error: (error) => {
        console.error('Error checking data source existence:', error);
      }
    });
  }

  private getDataSourceTypeId(connectionType: string): number {
    // Map connection types to numeric IDs
    // These would typically come from a reference table or enum
    switch (connectionType) {
      case 'excel': return 1;
      case 'csv': return 2;
      case 'parquet': return 3;
      case 'json': return 4;
      case 'api-listener': return 5;
      case 'api-retriever': return 6;
      default: return 0;
    }
  }

  onViewProfile(dataSource: DataSourceGet): void {
    if (dataSource.dataSourceId) {
      this.router.navigate(['/data/profile', dataSource.dataSourceId]);
    } else {
      console.error('Data source ID is missing');
    }
  }

  onEditDataSource(dataSource: DataSourceGet): void {
    const dialogRef = this.dialog.open(EditDataSourceDialogComponent, {
      width: '600px',
      data: { dataSource } as EditDataSourceDialogData
    });

    dialogRef.afterClosed().subscribe((result: EditDataSourceResult | undefined) => {
      if (result?.updated) {
        // Reload the data sources list to reflect changes
        this.loadDataSources();
      }
    });
  }

  onDeleteDataSource(dataSource: DataSourceGet): void {
    if (!dataSource.dataSourceId) {
      this.snackBar.open('Cannot delete data source: ID is missing', '×', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Data Source',
        message: `Are you sure you want to delete "${dataSource.dataSourceName}"? This action cannot be undone and may affect any projects using this data source.`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      } as ConfirmDialogData
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed && dataSource.dataSourceId) {
        this.dataService.deleteDataSource(dataSource.dataSourceId).subscribe({
          next: (success) => {
            if (success) {
              this.snackBar.open(`Data source "${dataSource.dataSourceName}" deleted successfully`, '×', {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: ['success-snackbar']
              });
              this.loadDataSources();
            } else {
              this.snackBar.open('Failed to delete data source', '×', {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          },
          error: (error) => {
            this.snackBar.open(error.message || 'Failed to delete data source', '×', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            console.error('Error deleting data source:', error);
          }
        });
      }
    });
  }
}
